import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/constants/routes';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { useMount } from 'src/helpers/hooks/useMount';
import { getPurchasePolicyAdditionalData } from 'src/redux/selectors/purchasePolicy';
import {
  loadExistingTimeUserFromStorage,
  saveExistingTimeUserToStorage,
} from 'src/storage/existing-time-user.storage';
import { Store } from 'src/types';

const ACCEPTED_START_ROUTES = new Set([
  '/playground',
  '/service',
  '/company',
  '/learn',
  '/compliance',
  '/compliance/welcome',
  '/building',
  '/organisation',
]);
const ROUTES_REDIRECTING_TO_LEARN_PAGE_IF_FIRST_TIME = new Set<string>([
  ROUTES.START_APPLICATION_PATH,
]);

interface RoutesRedirectorProps {
  children: ReactNode;
}

export function RoutesRedirector({ children }: RoutesRedirectorProps) {
  const navigate = useNavigate();
  const getUrlWithQueryParams = useLocationSearch();
  const purchasePolicyAdditionalData = useSelector(
    getPurchasePolicyAdditionalData,
  );
  const system = useSelector((state: Store) => state.system);
  const location = useLocation();

  useMount(() => {
    const pathname = location.pathname;
    const url = getUrlWithQueryParams(pathname);
    const currentRoute = '/' + pathname.split('/')[1];

    const shouldRedirectToAlreadyPurchasedPage =
      pathname !== ROUTES.CONFIRMATION_PATH &&
      pathname !== ROUTES.ALREADY_PURCHASED_PATH &&
      purchasePolicyAdditionalData.purchased;
    if (shouldRedirectToAlreadyPurchasedPage) {
      navigate(ROUTES.ALREADY_PURCHASED_PATH, { replace: true });
      return;
    }

    if (ACCEPTED_START_ROUTES.has(currentRoute)) {
      saveExistingTimeUserToStorage(ROUTES.START_APPLICATION_PATH);

      if (currentRoute == ROUTES.COMPLIANCE_PATH) return;

      navigate(url);
      return;
    }

    if (system.currentRoute) {
      navigate(system.currentRoute);
    }

    if (
      ROUTES_REDIRECTING_TO_LEARN_PAGE_IF_FIRST_TIME.has(currentRoute) &&
      !loadExistingTimeUserFromStorage()
    ) {
      saveExistingTimeUserToStorage(pathname);
      navigate(getUrlWithQueryParams(ROUTES.LEARN_PATH), { replace: true });
    }
  });

  return <>{children}</>;
}
